/* eslint-disable react/jsx-no-target-blank */
import { Box, Button } from "@mui/material";
import { JSX } from "react";
import spicyKoreanIllustration from "../assets/spicy-korean.jpeg";
import honeyGarlicIllustration from "../assets/honey-garlic.jpeg";
import mapleGingerIllustration from "../assets/maple-ginger.jpeg";
import spicyKoreanImg from "../assets/KoreanSpicy1.jpg";
import honeyGarlicImg from "../assets/HoneyGarlic1.jpg";
import mapleGingerImg from "../assets/MapleGinger.jpg";
import koreanDumplingsImg from "../assets/korean-dumplings.jpg";
import batteredWedgesImg from "../assets/battered-wedges.jpg";
import version1badges from "../assets/Version 1 Badges 5.svg";
import socialMediaImg from "../assets/social-media.png";
import whatsAppIcon from "../assets/whatsapp.png";

import FadeUp from "../components/FadeUp/FadeUp";
import { Link } from "react-router-dom";

function getImageSrc(value: string) {
  switch (value) {
    case "Spicy Korean Image":
      return spicyKoreanImg;
    case "Honey Garlic Image":
      return honeyGarlicImg;
    case "Maple Ginger Image":
      return mapleGingerImg;
    case "Version 1 Badges 5.svg":
      return version1badges;
    case "social-media.png":
      return socialMediaImg;
    default:
      return value;
  }
}

export function composeContent(
  obj: Record<string, any>,
  isMobileOrTablet: boolean,
) {
  let composedContent: JSX.Element[] = [];
  let foodMenuIllComposed = false;
  let foodMenuImgComposed = false;

  for (const [key, value] of Object.entries(obj)) {
    // Grid Section Component
    if (key.indexOf("-grid-section") > -1) {
      composedContent.push(
        <FadeUp key={key}>
          {composeGridSection(value, isMobileOrTablet)}
        </FadeUp>,
      );
      continue;
    }

    // Menu section with aligned item and price rows.
    if (key.indexOf("-menu-section") > -1) {
      composedContent.push(
        <FadeUp key={key}>
          {composeMenuSection(value, isMobileOrTablet)}
        </FadeUp>,
      );
      continue;
    }

    // Asset Image
    if (key.indexOf("asset-image") > -1) {
      composedContent.push(
        <FadeUp key={key}>
          <img
            src={getImageSrc(value)}
            alt={value}
            style={{ width: isMobileOrTablet ? "100%" : "40%" }}
          ></img>
        </FadeUp>,
      );
      continue;
    }

    if (key.indexOf("how-to-order-div") > -1) {
      if (key.indexOf("mobile-only") > -1 && !isMobileOrTablet) {
        continue;
      }

      if (key.indexOf("desktop-only") > -1 && isMobileOrTablet) {
        continue;
      }

      composedContent.push(
        <FadeUp key={key}>
          {key.indexOf("compact") > -1 || value?.display === "compact"
            ? composeCompactWhatsApp(value, isMobileOrTablet)
            : composeHowToOrder(value)}
        </FadeUp>,
      );
      continue;
    }

    // Button Component
    if (key.indexOf("-button") > -1) {
      if (key.indexOf("download-app-button") > -1) {
        composedContent.push(
          <Link to={"/download-app"} key={key}>
            {composeButton(value, isMobileOrTablet)}
          </Link>,
        );
      } else {
        composedContent.push(composeButton(value, isMobileOrTablet));
      }
      composedContent.push(composeSpaceBox());
      continue;
    }

    // Title Component
    if (key.indexOf("-title") > -1) {
      composedContent.push(composeTitle(value));
      continue;
    }

    // Story section Component
    if (key.indexOf("-section") > -1) {
      composedContent.push(composeTextSection(value));
      continue;
    }

    // Food Menu Illustrated Component
    if (key.indexOf("-food-menu-illustrated") > -1 && !foodMenuIllComposed) {
      let foodMenuContent: JSX.Element[] = [];
      const foodMenuIllItems = Object.fromEntries(
        Object.entries(obj).filter(
          ([k]) => k.indexOf("-food-menu-illustrated") > -1,
        ),
      );

      for (const [itemKey, itemValue] of Object.entries(foodMenuIllItems)) {
        foodMenuContent.push(
          composeFoodMenuIllItem(itemKey, itemValue, isMobileOrTablet),
        );
      }

      composedContent.push(
        composeFoodMenuIllRow(foodMenuContent, isMobileOrTablet),
      );
      foodMenuIllComposed = true;
      continue;
    }

    // Food Menu Image Component
    if (key.indexOf("-food-menu-image") > -1 && !foodMenuImgComposed) {
      let foodMenuContent: JSX.Element[] = [];
      const foodMenuIllItems = Object.fromEntries(
        Object.entries(obj).filter(([k]) => k.indexOf("-food-menu-image") > -1),
      );

      for (const [itemKey, itemValue] of Object.entries(foodMenuIllItems)) {
        foodMenuContent.push(
          composeFoodMenuImgItem(itemKey, itemValue, isMobileOrTablet),
        );
      }

      composedContent.push(
        composeFoodMenuImgRow(foodMenuContent, isMobileOrTablet),
      );

      foodMenuImgComposed = true;
    }
  }

  return composedContent;
}

function composeMenuSection(
  obj: Record<string, any>,
  isMobileOrTablet: boolean,
) {
  const sectionTitle =
    obj["section-title"] ?? obj["title"] ?? obj["heading"] ?? "";
  const itemEntries = Object.entries(obj)
    .filter(([key]) => key.startsWith("item-"))
    .sort(([a], [b]) => compareMenuItemKeys(a, b));

  return (
    <Box
      sx={{
        paddingBottom: isMobileOrTablet ? "28px" : "36px",
        textAlign: "left",
      }}
    >
      {sectionTitle ? (
        <Box
          sx={{
            fontFamily: '"PermanentMarker-Regular", sans-serif !important',
            fontSize: isMobileOrTablet ? "1rem" : "1.05rem",
            marginBottom: "14px",
            color: "#1f1f1f",
          }}
        >
          {sectionTitle}
        </Box>
      ) : null}

      <Box sx={{ display: "grid", gap: isMobileOrTablet ? 1.25 : 1.5 }}>
        {itemEntries.map(([itemKey, itemValue]) => {
          const menuItemName = getMenuItemName(itemValue);
          const menuItemPrice = getMenuItemPrice(itemValue);

          return (
            <Box
              key={itemKey}
              sx={{
                display: "grid",
                gridTemplateColumns: "minmax(0, 1fr) auto",
                columnGap: isMobileOrTablet ? 1.5 : 3,
                alignItems: "start",
                paddingBottom: "6px",
                borderBottom: "1px solid rgba(0, 0, 0, 0.08)",
              }}
            >
              <Box
                sx={{
                  color: "#262626",
                  fontSize: isMobileOrTablet ? "0.95rem" : "1rem",
                  lineHeight: 1.5,
                  minWidth: 0,
                  wordBreak: "break-word",
                }}
              >
                {menuItemName}
              </Box>

              <Box
                sx={{
                  color: "#4a4a4a",
                  fontSize: isMobileOrTablet ? "0.95rem" : "1rem",
                  fontFamily: '"InstrumentSans-SemiBold", sans-serif !important',
                  textAlign: "right",
                  whiteSpace: "nowrap",
                  minWidth: isMobileOrTablet ? "54px" : "72px",
                }}
              >
                {menuItemPrice}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

function compareMenuItemKeys(a: string, b: string) {
  const aNumber = Number(a.replace("item-", ""));
  const bNumber = Number(b.replace("item-", ""));

  if (!Number.isNaN(aNumber) && !Number.isNaN(bNumber)) {
    return aNumber - bNumber;
  }

  return a.localeCompare(b, undefined, { numeric: true });
}

function getMenuItemName(itemValue: any) {
  if (typeof itemValue === "string") {
    return itemValue;
  }

  return itemValue?.name ?? itemValue?.item ?? itemValue?.title ?? "";
}

function getMenuItemPrice(itemValue: any) {
  if (typeof itemValue === "string") {
    return "";
  }

  return itemValue?.price ?? itemValue?.value ?? "";
}

function composeGridSection(obj: any, isMobileOrTablet: boolean) {
  // Determine columns from config or default to 2
  const cols = obj.config?.columns || 2;
  const gridColumns = isMobileOrTablet ? "1fr" : `repeat(${cols}, 1fr)`;

  // Filter out the config key to get only items
  const items = Object.entries(obj).filter(([k]) => k.startsWith("item-"));

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: gridColumns,
        gap: 3,
        paddingY: "30px",
      }}
    >
      {items.map(([itemKey, itemValue]: [string, any]) => (
        <Box
          key={itemKey}
          sx={{
            backgroundColor: "#f5f2f2",
            borderRadius: "12px",
            overflow: "hidden",
            boxShadow: "0px 4px 10px rgba(0,0,0,0.05)",
            textAlign: "left",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Image */}
          {itemValue.image && (
            <Box
              sx={{
                height: "150px",
                backgroundImage: `url(${getImageSrc(itemValue.image)})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            />
          )}

          {/* Card Body */}
          <Box sx={{ p: 2 }}>
            <h4 style={{ margin: "0 0 8px 0", color: "#333" }}>
              {itemValue.title}
            </h4>

            {/* Dynamic Details */}
            {itemValue.details?.map((detail: string, i: number) => (
              <div
                key={i}
                style={{
                  color: "gray",
                  fontSize: "0.85rem",
                  marginBottom: "4px",
                  lineHeight: "1.2",
                }}
              >
                - {detail}
              </div>
            ))}
          </Box>
        </Box>
      ))}
    </Box>
  );
}

function composeCompactWhatsApp(obj: any, isMobileOrTablet: boolean) {
  const label = obj["label"] ?? "ORDER VIA WHATSAPP";

  return (
    <Box
      sx={{
        paddingBottom: "20px",
        textAlign: "center",
      }}
    >
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "10px",
          padding: isMobileOrTablet ? "12px 16px" : "14px 18px",
          borderRadius: "18px",
          backgroundColor: "#fff7e5",
          border: "1px solid rgba(224, 9, 51, 0.12)",
        }}
      >
        <img
          style={{
            width: "32px",
            height: "32px",
            flexShrink: 0,
          }}
          alt="whatsApp Icon"
          src={whatsAppIcon}
        />

        <Box sx={{ textAlign: "left", lineHeight: 1.2 }}>
          <div
            style={{
              fontSize: "0.78rem",
              color: "#6a6a6a",
              letterSpacing: "0.06em",
            }}
          >
            {label}
          </div>
          <a
            style={{
              display: "inline-block",
              fontSize: isMobileOrTablet ? "1.2rem" : "1.3rem",
              color: "#E00933",
              textDecoration: "none",
              fontFamily: '"InstrumentSans-SemiBold", sans-serif',
              marginTop: "2px",
            }}
            href={"https://wa.me/44" + obj["order-whatsapp-number"]}
            target="_blank"
          >
            {obj["order-whatsapp-number"]}
          </a>
        </Box>
      </Box>
    </Box>
  );
}

function composeHowToOrder(obj: any) {
  return (
    <Box sx={{ paddingBottom: "15px" }}>
      <FadeUp>
        <div>{obj["order-paragraph"]}</div>
        <Box sx={{ height: "25px" }} />

        <img
          style={{
            display: "inline-block",
            marginRight: "7px",
            width: "36px",
            verticalAlign: "bottom",
          }}
          alt="whatsApp Icon"
          src={whatsAppIcon}
        />

        <div
          style={{
            display: "inline-block",
          }}
        >
          <a
            style={{
              display: "inline-block",
              fontSize: "1.4rem",
              color: "#ffc858",
            }}
            href={"https://wa.me/44" + obj["order-whatsapp-number"]}
            target="_blank"
          >
            {obj["order-whatsapp-number"]}
          </a>
        </div>
        <Box sx={{ height: "25px" }} />

        <div style={{ fontSize: "0.85rem" }}>{obj["order-instructions"]}</div>
      </FadeUp>
    </Box>
  );
}

function composeButton(buttonText: string, isMobileOrTablet: boolean) {
  return (
    <Button
      sx={{
        marginTop: "-30px",
        height: "43px",
        backgroundColor: "#E00933",
        borderRadius: "100px",
        boxShadow: 2,
        color: "white",
        "&:hover": {
          backgroundColor: "#b9082b",
        },
        padding: "0 23px 0 23px",
        fontSize: isMobileOrTablet ? "11px" : "13px",
        fontWeight: "600",
        letterSpacing: "1px",
      }}
    >
      {buttonText}
    </Button>
  );
}

function composeTitle(titleText: string) {
  return (
    <FadeUp>
      <Box>
        <h6 className={"gan-chi-title"} style={{}}>
          {titleText}
        </h6>
      </Box>
    </FadeUp>
  );
}

function composeSpaceBox() {
  return (
    <Box
      sx={{
        height: "2vh",
        width: "100%",
      }}
    ></Box>
  );
}

function composeFoodMenuIllRow(
  content: JSX.Element[],
  isMobileOrTablet: boolean,
) {
  const itemsPerRow = isMobileOrTablet ? 2 : 3;
  const rows: JSX.Element[][] = [];

  for (let index = 0; index < content.length; index += itemsPerRow) {
    rows.push(content.slice(index, index + itemsPerRow));
  }

  return (
    <FadeUp>
      <Box sx={{ marginBottom: "50px" }}>
        {rows.map((row, rowIndex) => (
          <Box
            key={rowIndex}
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "start",
              gap: isMobileOrTablet ? 3 : 5,
              marginBottom:
                rowIndex === rows.length - 1 ? 0 : isMobileOrTablet ? 3 : 5,
              flexWrap: "nowrap",
            }}
          >
            {row}
          </Box>
        ))}
      </Box>
    </FadeUp>
  );
}

function composeFoodMenuImgRow(
  content: JSX.Element[],
  isMobileOrTablet: boolean,
) {
  const itemsPerRow = isMobileOrTablet ? 2 : 3;
  const rows: JSX.Element[][] = [];

  for (let index = 0; index < content.length; index += itemsPerRow) {
    rows.push(content.slice(index, index + itemsPerRow));
  }

  return (
    <FadeUp>
      <Box sx={{ marginBottom: "50px" }}>
        {rows.map((row, rowIndex) => (
          <Box
            key={rowIndex}
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "start",
              gap: isMobileOrTablet ? 3 : 5,
              marginBottom: rowIndex === rows.length - 1 ? 0 : isMobileOrTablet ? 3 : 5,
              flexWrap: "nowrap",
            }}
          >
            {row}
          </Box>
        ))}
      </Box>
    </FadeUp>
  );
}

function composeFoodMenuIllItem(
  foodMenuIllKey: string,
  foodMenuIllObj: Record<string, string>,
  isMobileOrTablet: boolean,
) {
  const illustration = (id: string) => {
    if (id?.indexOf("korean-spicy") > -1) return spicyKoreanIllustration;
    if (id?.indexOf("honey-garlic") > -1) return honeyGarlicIllustration;
    if (id?.indexOf("maple-ginger") > -1) return mapleGingerIllustration;
  };
  return (
    <Box sx={{ marginBottom: isMobileOrTablet ? "30px" : "0" }}>
      <img
        style={{
          height: isMobileOrTablet ? "auto" : "9vw",
          width: isMobileOrTablet ? "130px" : "9vw",
          borderRadius: "500px",
          marginBottom: isMobileOrTablet ? "5px" : "10px",
        }}
        src={illustration(foodMenuIllKey)}
        alt={foodMenuIllKey}
      />
      <Box>
        <span style={{ color: "gray", fontSize: "0.9rem" }}>
          {foodMenuIllObj["b-text"]}
        </span>
      </Box>
    </Box>
  );
}

function composeFoodMenuImgItem(
  foodMenuIllKey: string,
  foodMenuIllObj: Record<string, string>,
  isMobileOrTablet: boolean,
) {
  const image = (id: string) => {
    if (id?.indexOf("korean-spicy") > -1) return spicyKoreanImg;
    if (id?.indexOf("honey-garlic") > -1) return honeyGarlicImg;
    if (id?.indexOf("maple-ginger") > -1) return mapleGingerImg;
    if (id?.indexOf("korean-dumplings") > -1) return koreanDumplingsImg;
    if (id?.indexOf("battered-wedges") > -1) return batteredWedgesImg;
  };
  return (
    <Box
      style={{
        marginBottom: "20px",
      }}
    >
      <Box
        style={{
          height: isMobileOrTablet ? "130px" : "11vw",
          width: isMobileOrTablet ? "130px" : "11vw",
          borderRadius: "50px",
          margin: "auto",
          marginBottom: isMobileOrTablet ? "15px" : "10px",
          backgroundImage: "url(" + image(foodMenuIllKey) + ")",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      <Box>
        <span style={{ color: "gray", fontSize: "0.9rem" }}>
          {foodMenuIllObj["b-text"]}
        </span>
      </Box>
    </Box>
  );
}

function composeTextSection(obj: any) {
  const paragraphs = Object.entries(obj).filter(
    ([key]) => key.indexOf("-paragraph") > -1,
  );

  return (
    <Box sx={{ paddingBottom: "15px" }}>
      {paragraphs.map(([key, value]) => (
        <FadeUp key={key}>
          <div>{String(value)}</div>
        </FadeUp>
      ))}
    </Box>
  );
}




